import { HeaderService } from "@app/components/header/header.service";
import { Homeowner } from "@app/models/Homeowner";
import { HomeownersTermsAgreement } from "@app/models/HomeownersTermsAgreement";
import { PaymentRequest } from "@app/models/PaymentRequest";
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { HomeownerApi } from "@app/services/apiServices/HomeownerApi";
import { PaymentApi } from "@app/services/apiServices/PaymentApi";
import { SpinnerService } from "@app/services/spinner.service";
import { GuestPaymentStore } from "@app/services/stores/GuestPaymentStore";
import { PaymentEntityType } from "@app/models/PaymentEntityType";
import { take } from "rxjs/operators";
import { Component, Inject, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { OneButtonDialogComponent } from "@app/components/sharedDialogs/oneButtonDialog.component";
import { PaymentErrorDialog } from "@app/components/sharedDialogs/dialogPaymentError.component";
import { PaymentDeclinedDialog } from "@app/components/sharedDialogs/dialogPaymentDeclined.component";
import { DialogPreCancelComponent } from "@app/components/sharedDialogs/dialogPreCancel.component";
import { AppDataService } from "@app/services/appData.service";
import { WindowToken } from "@app/util/window.token";
import { ActivatedRoute, Router } from "@angular/router";
import { AppDate } from "@app/util/app-date";
import { Property } from "@app/models/Property";
import { OneTimeOverlapDialog } from "@app/pages/payment/onetime-overlap.dialog";
import { PaymentHistoryApi } from "@app/services/apiServices/PaymentHistoryApi";
import { PaymentHistory } from "@app/models/PaymentHistory";
import { ValidPaymentStatusType } from "@app/services/shared/ValidPaymentStatusType";
import { FeatureFlagService } from "@app/services/feature-flag.service";

type PaymentOneTimeRange = {
    date: AppDate;
    amount: number;
    vendorItemNumber: number;
}

@Component({
    selector: 'app-guest-payment-confirmation',
    template: require('./guestPaymentConfirmation.html')
})

export class GuestPaymentConfirmationComponent implements OnInit {
    paymentRequest: PaymentRequest;
    homeowner: Homeowner;
    paymentConfirmationNumber: any;
    paymentId: any;
    isPaymentSubmitted: boolean;
    displayUnitNumber: boolean;
    paymentEntityType: PaymentEntityType;
    duplicateCheck: boolean = false;
    isAsyncFeatureEnabled: boolean = false;

    guestPaymentState: string = '/guestpayment';
    guestPaymentCardState: string = '/guestpaymentcard';
    guestPaymentCheckState: string = '/guestpaymentecheck';
    guestPaymentSuccessState: string = '/guestPaymentSuccess';

    $stateParams: any;

    constructor(
        @Inject(WindowToken) public window,
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(PaymentApi) private paymentApi: PaymentApi,
        @Inject(HeaderService) private headerService: HeaderService,
        @Inject(GuestPaymentStore) private guestPaymentStore: GuestPaymentStore,
        @Inject(HomeownerApi) private homeownerApi: HomeownerApi,
        @Inject(SpinnerService) private spinnerService: SpinnerService,
        @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
        @Inject(AppDataService) private appDataService: AppDataService,
        @Inject(Router) private router: Router,
        @Inject(ActivatedRoute) private route: ActivatedRoute,
        @Inject(PaymentHistoryApi) private paymentHistoryApi: PaymentHistoryApi,
        @Inject(FeatureFlagService) private featureFlagService: FeatureFlagService) {

        if(this.router.getCurrentNavigation()) {
            this.$stateParams = this.router.getCurrentNavigation().extras.state ?? null;
        }
    }

    ngOnInit() {
        this.headerService.setDisplayRightContent(false);

        this.paymentEntityType = this.route.snapshot.data.paymentEntityType;
        this.paymentRequest = this.guestPaymentStore.getPaymentRequest()
        this.homeowner = this.guestPaymentStore.getHomeowner();

        this.isAsyncFeatureEnabled = this.featureFlagService.isAsyncFeatureEnabled();

        if (this.paymentEntityType === PaymentEntityType.AMENITY) {
            this.guestPaymentState = '/amenities/guestpayment';
            this.guestPaymentCardState = '/amenities/guestpaymentcard';
            this.guestPaymentCheckState = '/amenities/guestpaymentecheck';
            this.guestPaymentSuccessState = '/amenities/guestPaymentSuccess';
        }

        if (!this.paymentRequest || !this.homeowner) {
            this.router.navigateByUrl(this.guestPaymentState, { state: { location: "replace" } });
        }

        this.displayUnitNumber = unitNumberExists(this.paymentRequest);

        this.isPaymentSubmitted = false;
    }

    /**
     * Retrieves property list data for the current user.
     *
     * @returns {promise<property[]>} A promise that resolves to an array of properties.
     */
    getPropertiesData(): Promise<Property[]> {
        return new Promise<Property[]>((resolve, reject) => {
            this.paymentHistoryApi.propertiesIdGuestPaymentHistoriesGet(this.homeowner.id, this.paymentRequest.property.id).pipe(take(1))
                .subscribe({
                    next: (resp) => {
                        resolve(resp);
                    }, error: (err) => {
                        if (err.status === 404) {
                            resolve([]);
                        }
                        reject();
                    }
                });
        });
    }

    /**
     * Checks for overlapping payments in the user's payment history.
     *
     * @param {boolean} value - A boolean value
     * @returns {promise<boolean>}A promise that resolves to true if overlapping payments are found , otherwise false.
     *
     */
    checkForOverlap(): Promise<{ value: boolean }> {
        return new Promise<{ value: boolean }>((resolve) => {
            this.getPropertiesData().then((paymentHistory: PaymentHistory[]) => {
                if (paymentHistory.length > 0) {
                    let overlapOnetimePayments: PaymentOneTimeRange[] = [];
                    let startDate = AppDate.now().subrtactMinutes(1440);
                    for (let key in paymentHistory) {
                        const oneTimeDate = AppDate.fromString(paymentHistory[key].date.formattedFullDate, 'MM/dd/yyyy');
                        const amount = this.paymentRequest.payment.principalAmount;
                        if ((startDate.fullIndex <= oneTimeDate.fullIndex )
                            && (paymentHistory[key].principalAmount === amount) && (paymentHistory[key].paymentEntityType === this.paymentRequest.paymentEntityType)
                            && Object.values(ValidPaymentStatusType).includes(paymentHistory[key].status as ValidPaymentStatusType)) {
                            overlapOnetimePayments.push({ date: oneTimeDate, amount: paymentHistory[key].amount, vendorItemNumber: paymentHistory[key].vendorItemNumber });
                        }
                    }
                    if (overlapOnetimePayments.length > 0) {
                        const dialogRef = this.dialog.open(OneTimeOverlapDialog, {
                            data: { currentDate: overlapOnetimePayments[0].date, amount: overlapOnetimePayments[0].amount, vendorItemNumber: overlapOnetimePayments[0].vendorItemNumber }
                        })
                        dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
                            this.duplicateCheck = !!value;
                            resolve({ value: !!value });
                        })
                    } else {
                        resolve({ value: true });
                    }
                } else {
                    resolve({ value: true });
                }

            });
        });
    }

    submit() {
        this.isPaymentSubmitted = true;
        this.checkForOverlap().then((result) => {
            if (result.value === true) {
                this.spinnerService.showSpinner();

                // **************************************/
                let today = new Date();
                let mm = ("0" + (today.getMonth() + 1)).slice(-2);
                let dd = ('0' + today.getDate()).slice(-2);
                let formattedDate = today.getFullYear() + "-" + mm + "-" + dd;

                this.paymentRequest.firstName = this.homeowner.firstName;
                this.paymentRequest.lastName = this.homeowner.lastName;
                this.paymentRequest.duplicateCheck = this.duplicateCheck;
                if (this.homeowner.contactInformation && this.homeowner.contactInformation.email) {
                    this.paymentRequest.email = this.homeowner.contactInformation.email.value;
                }
                this.paymentRequest.correlationId = this.guestPaymentStore.getCorrelationId();
                this.guestPaymentStore.setPaymentRequest(this.paymentRequest)

                const hoTermsAgreement: HomeownersTermsAgreement = {
                    acceptedTermsDate: formattedDate,
                    acceptedTerms: true,
                    partyId: this.homeowner.id
                }

                /** Allows for general retry */
                const catchSubmitError = (err) => {
                    this.networkErrorService.handleNetworkError(err, true).pipe(take(1))
                        .subscribe((resp) => {
                            if (resp === true) {
                                this.submit();
                            }
                        });
                }

                const catchPaymentSubmitError = (err) => {
                    this.spinnerService.closeSpinner();
                    if (err.status === 409 && this.paymentEntityType === PaymentEntityType.AMENITY) {
                        let dialog = this.dialog.open(OneButtonDialogComponent, {
                            data: {
                                'header': 'Your payment was not processed.',
                                'message': 'Not enough inventory to complete the request.',
                                'message2': 'Click OK to go back and correct any problems with your request.',
                                'buttonText': 'OK'
                            }
                        });
                        dialog.afterClosed().subscribe(() => {
                            this.router.navigateByUrl('/amenities/select');
                        })
                    } else {
                        this.guestPaymentStore.clearAllGuest();
                        let dialog = this.dialog.open(PaymentErrorDialog);
                        dialog.afterClosed().subscribe(() => {
                            this.router.navigateByUrl(this.guestPaymentState);
                        })
                    }
                }

                this.homeownerApi.homeownersTermsAgreementPost(true, hoTermsAgreement).subscribe(() => {
                    return this.paymentApi.paymentsPost(true, this.paymentRequest).pipe(take(1)).subscribe((data) => {
                        this.spinnerService.closeSpinner();

                        // Check feature flag to determine which payment processing logic to use
                        if (this.isAsyncFeatureEnabled) {
                            if (data.message === "Payment accepted") {
                                this.router.navigateByUrl(this.guestPaymentSuccessState, { state: {
                                    isAsyncFlow: true,
                                } });
                            } else {
                                // Handle any non-success response as an error
                                console.error("PaymentApi paymentsPost unexpected response", data);
                                catchSubmitError("Payment failed");
                            }
                        } else {

                            if (data.vendorCardApprovalCode === "D") {
                                if(this.paymentRequest.paymentMethod.routingNumber != null) {
                                    // is ACH
                                    let dialog = this.dialog.open(PaymentErrorDialog, {
                                        data: { errorMessage: data.userResponseMessage }
                                    });
                                    dialog.afterClosed().subscribe(() => {
                                        this.guestPaymentStore.clearAchTokenRequest();
                                        this.router.navigateByUrl(this.guestPaymentCheckState);
                                    })
                                } else {
                                    // is CARD
                                    let dialog = this.dialog.open(PaymentDeclinedDialog, {
                                        data: { errorMessage: data.userResponseMessage }
                                    });
                                    dialog.afterClosed().subscribe(() => {
                                        this.guestPaymentStore.clearCardUpdate();
                                        this.router.navigateByUrl(this.guestPaymentCardState);
                                    })
                                }

                            } else if (data.vendorCardApprovalCode === "E") {
                                let dialog = this.dialog.open(PaymentErrorDialog);
                                dialog.afterClosed().subscribe(() => {
                                    if (this.paymentRequest.paymentMethod.routingNumber != null) {
                                        // is ACH
                                        // do nothing
                                    } else {
                                        // is CARD
                                        this.guestPaymentStore.clearCardUpdate();
                                        this.router.navigateByUrl(this.guestPaymentCardState);
                                    }
                                })

                            } else if (data.confirmationNumber) {
                                this.paymentConfirmationNumber = data.confirmationNumber;
                                this.paymentId = data.id;

                                this.router.navigateByUrl(this.guestPaymentSuccessState, { state: {
                                    paymentConfirmationNumber: data.confirmationNumber,
                                    paymentId: data.id
                                } });

                            } else {
                                console.error("PaymentApi paymentsPost error response", data);
                                catchSubmitError("Failed to post payment");
                            }
                        }

                    }, catchPaymentSubmitError)
                }, catchSubmitError);
            } else {
                this.goToWelcomePage();
            }
        });
    }

    onCancel() {
        this.guestPaymentStore.clearCorrelationId();
        const dialog = this.dialog.open(DialogPreCancelComponent, {
            data: { paymentAmount: this.paymentRequest.payment.totalPaymentAmount }
        });
        dialog.afterClosed().subscribe((value) => {
            if (value === 'cancel') {
                // allow them to cancel and edit the existing
                if (this.paymentRequest.paymentMethod.routingNumber != null) {
                    // is ACH
                    this.router.navigateByUrl(this.guestPaymentCheckState);
                } else {
                    // is CARD
                    this.guestPaymentStore.clearCardUpdate();
                    this.router.navigateByUrl(this.guestPaymentCardState);
                }
            }
        });
    }

    goToWelcomePage() {
        this.guestPaymentStore.clearAllGuest();
        const webUrl = this.appDataService.appConfig.get_APP_ENV().HOAPASS_WEB_URL
        this.window.location.replace(webUrl)
    }

}

export function unitNumberExists(paymentRequest: PaymentRequest): boolean {
    return paymentRequest?.property?.address?.unit?.length > 0
}

